import { useState, useEffect } from "react";
import { List, X } from "lucide-react";

interface TOCItem {
  id: string;
  title: string;
}

interface BlogTableOfContentsProps {
  items: TOCItem[];
  collapsible?: boolean;
}

export const BlogTableOfContents = ({ items, collapsible = false }: BlogTableOfContentsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!collapsible) return;
    
    const handleScroll = () => {
      // Show TOC icon after scrolling 400px
      setIsVisible(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [collapsible]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    if (collapsible) {
      setIsExpanded(false);
    }
  };

  // Collapsible mode - only show after scroll
  if (collapsible) {
    if (!isVisible) return null;

    if (!isExpanded) {
      return (
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-card border border-border rounded-xl p-3 hover:bg-muted transition-colors shadow-lg"
          aria-label="Open table of contents"
        >
          <List className="w-5 h-5 text-primary" />
        </button>
      );
    }

    return (
      <nav className="bg-card border border-border rounded-2xl p-5 shadow-lg animate-in fade-in slide-in-from-right-2 duration-200">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <List className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-display font-bold">In This Article</h2>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="p-1 hover:bg-muted rounded transition-colors"
            aria-label="Close table of contents"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <ul className="space-y-1.5">
          {items.map((item, index) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className="flex items-start gap-2 text-muted-foreground hover:text-primary transition-colors font-body group text-sm"
              >
                <span className="text-primary/60 font-medium text-xs mt-0.5">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="group-hover:underline underline-offset-2 leading-snug">
                  {item.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  // Non-collapsible mode (mobile inline TOC)
  return (
    <nav className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <List className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-display font-bold">In This Article</h2>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className="flex items-start gap-2 text-muted-foreground hover:text-primary transition-colors font-body group text-sm"
            >
              <span className="text-primary/60 font-medium text-xs mt-0.5">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="group-hover:underline underline-offset-2 leading-snug">
                {item.title}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};